import React from 'react';
import { motion } from 'framer-motion';
import { LuScanFace, LuTrendingUp, LuUsers } from 'react-icons/lu';
import { COPY } from '../../contracts/copyMap';

// The three product claims, icon + name + one line each.
//
// Extracted from MarketingHero so the mobile splash can render them too.
// On desktop they sit in the marketing column; on mobile they sit BELOW the
// CTA, which is the only placement that works there — measured earlier, putting
// them above the fold pushed the primary action off-screen entirely.
//
// ── COMPACT DROPS THE SUBTEXT ───────────────────────────────────────────────
//
// On the mobile splash the stack is bottom-anchored under a full-bleed portrait,
// and at three lines plus three sub-lines it grew tall enough to climb back up
// over the face — the subtext landed on the mesh and neither read. The sub-lines
// are the first thing to go: the NAMES carry the claim ("PRECISION SCANS",
// "PRIVATE & ANONYMOUS" are self-describing), while "Advanced AI face analysis."
// is elaboration, and elaboration is what a splash can afford to lose.
//
// It also tightens the row gap and the icon, because removing the second line
// makes each row shorter and the original 22px then reads as drift rather than
// rhythm.
//
// Props:
//   className  layout/visibility from the caller. Desktop passes the column's
//              `hidden lg:flex`; the mobile splash renders them unconditionally
//              below the button.
//   delayFrom  stagger index to start from, so the entrance continues the
//              hero's existing cascade instead of restarting it.
//   compact    name only, tighter rhythm. For the mobile splash.
const stagger = {
  hidden: { opacity: 0, y: 14 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const FEATURES = [
  { name: COPY.MARKETING_FEATURE_1_NAME, body: COPY.MARKETING_FEATURE_1_BODY, icon: <LuScanFace size={20} /> },
  { name: COPY.MARKETING_FEATURE_2_NAME, body: COPY.MARKETING_FEATURE_2_BODY, icon: <LuTrendingUp size={20} /> },
  { name: COPY.MARKETING_FEATURE_3_NAME, body: COPY.MARKETING_FEATURE_3_BODY, icon: <LuUsers size={20} /> },
];

const FeatureHighlightStack = ({ className = '', delayFrom = 4, compact = false }) => (
  <ul
    className={[
      'flex-col m-0 p-0 list-none',
      compact ? 'gap-3.5' : 'gap-[22px]',
      className,
    ].filter(Boolean).join(' ')}
  >
    {FEATURES.map((f, i) => (
      <motion.li
        key={f.name}
        className={`flex items-center ${compact ? 'gap-3.5' : 'gap-4'}`}
        variants={stagger}
        initial="hidden"
        animate="show"
        custom={delayFrom + i}
      >
        <span
          className={`inline-flex items-center justify-center shrink-0 rounded-full border border-[rgba(255,255,255,0.22)] text-[#E8E2D6] ${
            compact ? 'w-[38px] h-[38px]' : 'w-[46px] h-[46px]'
          }`}
        >
          {f.icon}
        </span>
        <span className="flex flex-col">
          <span className="font-display text-bone" style={{ fontSize: 17 }}>
            {f.name}
          </span>
          {!compact && (
            <span className="font-display text-bone-dim mt-px" style={{ fontSize: 15 }}>
              {f.body}
            </span>
          )}
        </span>
      </motion.li>
    ))}
  </ul>
);

export default FeatureHighlightStack;
